import { useEffect, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ArrowRight, Palette, Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import chrisPhoto from "@/assets/chris-heidlebaugh-colorado-web-designer.png";

/**
 * "Note from Chris" exit-intent / scroll / time popup.
 *
 * Triggers (after an 8s warm-up, whichever fires first):
 *   1. Desktop exit-intent (cursor leaves viewport at top edge)
 *   2. Scroll past 60% of page height
 *   3. 45 seconds elapsed on the page
 *
 * Suppression:
 *   - sessionStorage `cwi_popup_shown` — once per session
 *   - localStorage  `cwi_popup_dismissed_until` — 7 days after dismiss,
 *     30 days after CTA click (they converted)
 *
 * Excluded routes: /free-audit, /contact, /thank-you, /admin/*, /auth/*
 */

const SESSION_KEY = "cwi_popup_shown";
const DISMISS_KEY = "cwi_popup_dismissed_until";
const WARMUP_MS = 8_000;
const DISMISS_DAYS = 7;
const CONVERT_DAYS = 30;

const EXCLUDED_PREFIXES = [
  "/free-audit",
  "/free-mockup",
  "/marketing-plans",
  "/contact",
  "/thank-you",
  "/admin",
  "/auth",
];

const CTA_HREF =
  "/free-audit?utm_source=site&utm_medium=popup&utm_campaign=exit_intent";

function isExcludedPath(pathname: string): boolean {
  return EXCLUDED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

function isSuppressed(): boolean {
  if (typeof window === "undefined") return true;
  try {
    if (sessionStorage.getItem(SESSION_KEY) === "1") return true;
    const until = localStorage.getItem(DISMISS_KEY);
    if (until && Number(until) > Date.now()) return true;
  } catch {
    // storage blocked — fail open (don't show, less annoying than spamming)
    return true;
  }
  return false;
}

function suppressFor(days: number) {
  try {
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + days * 24 * 60 * 60 * 1000),
    );
  } catch {
    // ignore
  }
}

function markShownThisSession() {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    // ignore
  }
}

export function ExitIntentPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Don't even mount the listeners on excluded pages.
  const excluded = isExcludedPath(pathname);

  useEffect(() => {
    if (excluded) return;
    if (typeof window === "undefined") return;
    if (isSuppressed()) return;

    let armed = false;
    let fired = false;

    const trigger = (_reason: "exit" | "back") => {
      if (fired) return;
      fired = true;
      markShownThisSession();
      setOpen(true);
    };

    const onMouseLeave = (e: MouseEvent) => {
      if (!armed || fired) return;
      // Only treat as exit-intent when crossing the top edge.
      if (e.clientY <= 0) trigger("exit");
    };

    // Desktop pointer detection — exit-intent only on real cursors.
    const isDesktopPointer =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: fine)").matches;

    // Mobile back-button intent: push a sentinel state so the first Back
    // press fires popstate instead of leaving the site.
    let backStatePushed = false;
    const onPopState = () => {
      if (!armed || fired) return;
      trigger("back");
    };

    const warmup = window.setTimeout(() => {
      armed = true;
      if (isDesktopPointer) {
        document.addEventListener("mouseleave", onMouseLeave);
      } else {
        // Mobile-only: back-button exit intent.
        try {
          window.history.pushState(
            { cwiPopupSentinel: true },
            "",
            window.location.href,
          );
          backStatePushed = true;
          window.addEventListener("popstate", onPopState);
        } catch {
          // ignore — history API blocked
        }
      }
    }, WARMUP_MS);

    return () => {
      window.clearTimeout(warmup);
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("popstate", onPopState);
      // Clean up the sentinel history entry if still on top of stack.
      if (backStatePushed && window.history.state?.cwiPopupSentinel) {
        try {
          window.history.back();
        } catch {
          // ignore
        }
      }
    };
  }, [excluded, pathname]);


  if (excluded) return null;

  const handleDismiss = () => {
    suppressFor(DISMISS_DAYS);
    setOpen(false);
  };

  const handleConvert = () => {
    suppressFor(CONVERT_DAYS);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleDismiss();
        else setOpen(true);
      }}
    >
      <DialogContent
        className="max-w-[720px] gap-0 overflow-hidden border-0 p-0 shadow-elegant sm:rounded-2xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* a11y title/description (visually hidden) */}
        <DialogTitle className="sr-only">
          Free website mockup or digital marketing audit from Chris
        </DialogTitle>
        <DialogDescription className="sr-only">
          Get a free 15-minute marketing audit from Chris Heidlebaugh, founder
          of Colorado Web Impressions. No sales pitch, no contract.
        </DialogDescription>

        {/* Top gold accent bar */}
        <div className="h-1.5 w-full bg-brand-gold-deep" />

        <div className="grid grid-cols-1 sm:grid-cols-[260px,1fr]">
          {/* Photo column */}
          <div className="relative flex flex-col items-center justify-center bg-brand-cream px-6 py-8 sm:py-10">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 opacity-60"
              style={{
                background:
                  "radial-gradient(ellipse at top left, oklch(0.74 0.09 80 / 0.18), transparent 60%)",
              }}
            />
            <div className="relative">
              <div className="rounded-full p-1 ring-2 ring-brand-gold-deep">
                <img
                  src={chrisPhoto}
                  alt="Chris Heidlebaugh, Founder of Colorado Web Impressions — Colorado Springs web designer and SEO expert"
                  width={160}
                  height={160}
                  loading="lazy"
                  className="h-32 w-32 rounded-full object-cover sm:h-40 sm:w-40"
                  data-geo-lat="38.9359976"
                  data-geo-lng="-104.7653118"
                  data-geo-city="Colorado Springs"
                />
              </div>
            </div>
            <p className="relative mt-4 font-serif text-lg italic text-foreground">
              — Chris
            </p>
            <p className="relative mt-2 text-xs font-bold uppercase tracking-wider text-foreground">
              Chris Heidlebaugh
            </p>
            <p className="relative text-xs text-muted-foreground">
              Founder, Colorado Web Impressions
            </p>
          </div>

          {/* Copy column */}
          <div className="relative flex flex-col bg-card px-6 py-8 sm:px-8 sm:py-10">
            {/* Close X */}
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Close popup"
              className="absolute right-3 top-3 rounded-full p-2 text-muted-foreground transition-smooth hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-brand-black px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-brand-gold-bright">
              Free · No Contract · No Sales Pitch
            </div>
            <h2 className="mt-4 pr-6 text-2xl font-extrabold leading-tight text-foreground sm:text-[26px]">
              Before you go — pick a{" "}
              <span className="text-brand-gold-deep">free gift</span> from Chris
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              27 years helping Colorado businesses. Choose what helps you most.
            </p>
            <div className="mt-5 grid gap-3">
              {[
                {
                  href: "/free-mockup?utm_source=site&utm_medium=popup&utm_campaign=exit_intent",
                  icon: Palette,
                  title: "Free Website Mockup",
                  text: "See a custom homepage design before you spend a dime.",
                },
                {
                  href: CTA_HREF,
                  icon: Search,
                  title: "Free Digital Marketing Audit",
                  text: "A real video review of your site, Google Maps and search visibility.",
                },
              ].map((o) => (
                <a
                  key={o.title}
                  href={o.href}
                  onClick={handleConvert}
                  className="group flex items-center gap-3 rounded-xl border-2 border-border bg-background p-4 transition-smooth hover:border-brand-gold-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-brand-black text-brand-gold-bright">
                    <o.icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-foreground">{o.title}</span>
                    <span className="block text-xs leading-snug text-muted-foreground">{o.text}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                </a>
              ))}
            </div>

            <Button asChild variant="link" className="mt-3 h-auto whitespace-normal text-center text-brand-gold-ink">
              <Link to="/marketing-plans" onClick={() => setOpen(false)}>
                See how your free audit becomes a marketing plan <ArrowRight />
              </Link>
            </Button>

            {/* Dismiss link */}
            <button
              type="button"
              onClick={handleDismiss}
              className="mt-3 self-center text-xs text-muted-foreground underline-offset-2 transition-smooth hover:text-foreground hover:underline"
            >
              No thanks, I&apos;m just browsing
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
